// O que o follow-up fez: e-mails automáticos (com o texto que saiu) e tarefas
// criadas para o responsável. A resposta do cliente aparece quando alguém marca
// "Cliente respondeu" — ler a caixa do Outlook depende da autorização no
// Microsoft 365 (docs/followups-propostas.md).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp, Button, Segmented, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { fmtMomentoBr } from '../../utils/dataFmt';
import { fu, erroDe, type Envio } from './api';

const { Text, Paragraph } = Typography;

const STATUS: Record<Envio['status'], { cor: string; rotulo: string }> = {
  enviado: { cor: 'green', rotulo: 'enviado' },
  enviando: { cor: 'blue', rotulo: 'enviando' },
  falhou: { cor: 'red', rotulo: 'não saiu' },
  criada: { cor: 'geekblue', rotulo: 'tarefa criada' },
  incerto: { cor: 'orange', rotulo: 'pode ter saído — conferir' },
  cancelada: { cor: 'default', rotulo: 'tarefa cancelada' },
};

export function TabelaEnvios({ itens, carregando, comNegocio, onAbrirNegocio }: {
  itens: Envio[];
  carregando?: boolean;
  comNegocio: boolean;
  onAbrirNegocio?: (id: number) => void;
}) {
  const colunas: ColumnsType<Envio> = [
    { title: 'Quando', dataIndex: 'criado_em', width: 140, render: (v: string) => fmtMomentoBr(v) },
    ...(comNegocio ? [{
      title: 'Negócio', key: 'negocio', width: 260,
      render: (_: unknown, e: Envio) => (
        <div style={{ minWidth: 0 }}>
          {onAbrirNegocio
            ? <Button type="link" size="small" style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
                onClick={() => onAbrirNegocio(e.proposta_id)}>{e.titulo}</Button>
            : e.titulo}
          <div><Text type="secondary" style={{ fontSize: 12 }}>{e.cliente || ''}</Text></div>
        </div>
      ),
    }] : []),
    {
      title: 'O quê', key: 'tipo', width: 170,
      render: (_, e) => (
        <div>
          <Tag color={STATUS[e.status]?.cor} style={{ marginInlineEnd: 4 }}>{e.tipo === 'email' ? '✉️ e-mail' : '📋 tarefa'}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>nº {e.tentativa}</Text>
          {e.status !== 'enviado' && e.status !== 'criada' && <div><Tag color={STATUS[e.status]?.cor}>{STATUS[e.status]?.rotulo || e.status}</Tag></div>}
        </div>
      ),
    },
    {
      title: 'Detalhe', key: 'detalhe',
      render: (_, e) => e.tipo === 'email'
        ? <div style={{ minWidth: 0 }}>
            <div><Text strong>{e.assunto}</Text></div>
            <Text type="secondary" style={{ fontSize: 12 }}>de {e.remetente} para {(e.destinatarios || []).join(', ')}</Text>
            {e.erro && <div><Text type="danger" style={{ fontSize: 12 }}>{e.erro}</Text></div>}
          </div>
        : <Text type="secondary" style={{ fontSize: 12 }}>
            Tarefa para {e.remetente || 'o responsável'}{e.erro ? ` — o e-mail automático não saiu: ${e.erro}` : ''}
          </Text>,
    },
    {
      title: 'Resposta', key: 'resposta', width: 200,
      render: (_, e) => e.respondido_em
        ? <div><Tag color="purple">📩 {fmtMomentoBr(e.respondido_em)}</Tag>
            {e.resposta_resumo && <div><Text style={{ fontSize: 12 }}>{e.resposta_resumo}</Text></div>}</div>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table rowKey="id" size="small" loading={carregando} dataSource={itens} columns={colunas}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        expandable={{
          rowExpandable: e => e.tipo === 'email' && !!e.corpo,
          expandedRowRender: e => <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0, maxWidth: 720 }}>{e.corpo}</Paragraph>,
        }}
        locale={{ emptyText: 'Nenhum follow-up registrado no período.' }} />
    </div>
  );
}

export default function AbaEnviados() {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [tipo, setTipo] = useState<'todos' | 'email' | 'tarefa'>('todos');
  const [itens, setItens] = useState<Envio[]>([]);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setItens((await fu.enviados(dias, tipo === 'todos' ? undefined : tipo)).itens); }
    catch (e) { message.error(`Não consegui carregar: ${erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [dias, tipo, message]);
  useEffect(() => { carregar(); }, [carregar]);

  const emails = itens.filter(e => e.tipo === 'email');
  const respondidos = emails.filter(e => e.respondido_em).length;
  const falhas = emails.filter(e => e.status === 'falhou').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Segmented value={tipo} onChange={v => setTipo(v as typeof tipo)}
          options={[{ value: 'todos', label: 'Tudo' }, { value: 'email', label: 'E-mails' }, { value: 'tarefa', label: 'Tarefas' }]} />
        <Select value={dias} onChange={setDias} style={{ width: 150 }}
          options={[7, 30, 90, 365].map(d => ({ value: d, label: `Últimos ${d} dias` }))} />
        <Text type="secondary">
          {emails.length} e-mail(s) · {respondidos} com resposta marcada{falhas ? ` · ${falhas} não saíram` : ''}
        </Text>
        <div style={{ flex: 1 }} />
        <Button onClick={carregar} loading={carregando}>Atualizar</Button>
      </div>
      <TabelaEnvios itens={itens} carregando={carregando} comNegocio
        onAbrirNegocio={(id) => {
          try { sessionStorage.setItem('abrir_proposta_id', String(id)); } catch { /* sem storage */ }
          navigate('/comercial/pipeline');
        }} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        A resposta do cliente chega na caixa de quem enviou (o e-mail sai com “responder para” o responsável e cópia para ele).
        Marque “Cliente respondeu” na fila ou no negócio — ler a caixa do Outlook sozinho depende de autorização no Microsoft 365.
      </Text>
    </div>
  );
}
